import { writable, get } from "svelte/store";
import { apiServer } from "../stores";
import { getApi } from "./api";

// Stores
export const mqttClients = writable([]);        // MQTT 탭: 브로커 연결 클라이언트 목록
export const redisDevices = writable({});       // Redis 탭: Redis 저장 디바이스 상태
export const mqttDevices = writable({});        // SSE 업데이트용 (상세 정보)
export const mqttSchedules = writable([]);
export const mqttConnected = writable(false);
export const mqttEvents = writable([]);
export const brokerInfo = writable({ clientCount: 0, deviceCount: 0 });

const MAX_EVENT_LOG = 100;
let eventSource = null;

function getBaseUrl() {
    const server = get(apiServer);
    const protocol = server.includes("localhost") ? "http" : "https";
    return `${protocol}://${server}`;
}

// 1. 브로커 상태 로드 (clients + devices 모두)
export async function loadBrokerStatus() {
    try {
        const res = await getApi("/mqtt/monitor/broker-status");

        // 브로커 정보
        if (res.broker) {
            brokerInfo.set(res.broker);
        }

        // MQTT 탭용: clients 배열
        if (res.clients) {
            mqttClients.set(res.clients);
        }

        // Redis 탭용: devices 배열 → map으로 변환
        if (res.devices) {
            const deviceMap = {};
            res.devices.forEach(d => {
                deviceMap[d.mqtt_id] = d;
            });
            redisDevices.set(deviceMap);
            mqttDevices.set(deviceMap); // SSE 업데이트 베이스
        }

        return res;
    } catch (error) {
        console.error("Failed to load broker status:", error);
        return null;
    }
}

// 2. 초기 데이터 로드 (스케줄 포함)
export async function loadInitialData() {
    try {
        const [brokerRes, schedulesRes] = await Promise.all([
            loadBrokerStatus(),
            getApi("/mqtt/monitor/schedules"),
        ]);

        if (schedulesRes.data) {
            mqttSchedules.set(schedulesRes.data);
        }
    } catch (error) {
        console.error("Failed to load initial MQTT data:", error);
    }
}

// 3. Realtime: SSE
export function connectSSE() {
    if (eventSource) eventSource.close();

    const url = `${getBaseUrl()}/mqtt/monitor/stream`;
    console.log("Connecting SSE to:", url);

    eventSource = new EventSource(url);

    eventSource.onopen = () => {
        console.log("SSE Connected");
        // We wait for the 'connected' event for the official connected state
    };

    eventSource.addEventListener("connected", (e) => {
        console.log("SSE Handshake complete:", e.data);
        mqttConnected.set(true);
    });

    eventSource.addEventListener("mqtt", (e) => {
        try {
            const event = JSON.parse(e.data);
            applyRealtimeEvent(event);
        } catch (err) {
            console.error("Failed to parse MQTT event:", err);
        }
    });

    eventSource.addEventListener("keepalive", () => {
        // Heartbeat received
    });

    eventSource.onerror = (err) => {
        console.error("SSE Error:", err);
        mqttConnected.set(false);
        // EventSource automatically tries to reconnect
    };
}

export function disconnectSSE() {
    if (eventSource) {
        eventSource.close();
        eventSource = null;
    }
    mqttConnected.set(false);
}

// SSE event -> Svelte store update
function applyRealtimeEvent(event) {
    mqttDevices.update(devices => {
        const device = devices[event.device_id];

        // If device doesn't exist in our map yet (new device?), we might want to fetch list again or ignore.
        // For now, let's ignore or create a partial entry if we decide to support auto-discovery of new devices.
        if (!device) return devices;

        switch (event.event_type) {
            case "online":
                device.online = "true";
                break;
            case "offline":
                device.online = "false";
                break;
            case "power":
                device.power = event.payload;
                break;
            case "status":
                // stat/{id}/{field} -> update matching field
                const parts = event.topic.split("/");
                if (parts.length >= 3) {
                    const field = parts[2].toLowerCase(); // e.g. "dimmer", "ct", "color"

                    // Map Tasmota fields to our struct fields if names differ
                    // Our struct has lowercase fields: dimmer, color, colortemp (from redis mapping)
                    // Redis mapping:
                    // Dimmer -> dimmer
                    // Color -> color
                    // CT -> color_temp

                    if (field === "result") {
                        // Some status messages come as JSON in RESULT
                        try {
                            const payloadObj = JSON.parse(event.payload);
                            if (payloadObj.POWER) device.power = payloadObj.POWER;
                            if (payloadObj.Dimmer) device.dimmer = String(payloadObj.Dimmer);
                            if (payloadObj.Color) device.color = payloadObj.Color;
                            if (payloadObj.CT) device.color_temp = String(payloadObj.CT);
                            if (payloadObj.White) device.white = String(payloadObj.White);
                        } catch (e) {
                            console.warn("Failed to parse RESULT JSON:", e);
                        }
                    } else if (field === "dimmer") {
                        device.dimmer = event.payload;
                    } else if (field === "ct") {
                        device.color_temp = event.payload;
                    } else if (field === "color") {
                        device.color = event.payload;
                    }
                }
                break;
        }

        device.last_seen = event.timestamp;

        // Return a new object to trigger store update
        const newDevices = { ...devices };
        newDevices[event.device_id] = { ...device };
        return newDevices;
    });

    // Event log
    mqttEvents.update(events => {
        // Append new event to the end, then slice from the end to keep MAX_EVENT_LOG
        const newEvents = [...events, event];
        if (newEvents.length > MAX_EVENT_LOG) {
            return newEvents.slice(newEvents.length - MAX_EVENT_LOG);
        }
        return newEvents;
    });
}
